import { BarChart, type BarChartSeries } from '@mantine/charts';
import {
	type BoxProps,
	Group,
	SegmentedControl,
	type SegmentedControlItem,
	Skeleton,
	Stack,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paper } from '../../../components/Paper';
import { queryWithFilter } from '../../../util/api/queryWithFilter';
import { formatToCurrency } from '../../../util/formatting';
import type { FilterDto } from '../../../util/types/FilterDto';
import type { ApiResponse } from '../../../util/types/Response';
import type { AverageGraphDto } from '../types/AverageGraphDto';

interface TopTenProps extends BoxProps {
	filter: FilterDto;
}

const timeSpanOptions: SegmentedControlItem[] = [
	{ value: 'hourly', label: 'Hourly' },
	{ value: 'daily', label: 'Daily' },
];

const displayOptions: SegmentedControlItem[] = [
	{ value: 'transactions', label: 'Transactions' },
	{ value: 'sales', label: 'Sales' },
];

const salesSeries: BarChartSeries[] = [
	{
		label: 'Average Sales',
		name: 'sales',
		color: 'medepos-secondary.0',
	},
];

const transactionsSeries: BarChartSeries[] = [
	{
		label: 'Transactions',
		name: 'transactions',
		color: 'medepos-secondary.1',
	},
];

export function Averages({ filter, ...boxProps }: TopTenProps) {
	const { organisation } = useParams();
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		setIsReady(true);
	}, []);

	const [timeSpanOption, setTimeSpanOptions] = useState(
		timeSpanOptions[0].value,
	);

	const [displayOption, setDisplayOption] = useState(displayOptions[0].value);

	const topTenQuery = queryWithFilter<AverageGraphDto[]>(
		`${organisation}/dashboard/average/${timeSpanOption}`,
		filter,
		true,
	);

	const defaultChartProps = {
		...boxProps,
		data: mapSalesToChartFormat(displayOption, topTenQuery.data),
		dataKey: 'name',
		xAxisProps: {
			hide: true,
		},
	};

	return (
		<Paper
			title={'AVERAGES'}
			headerContent={
				<Group>
					<SegmentedControl
						value={timeSpanOption}
						onChange={setTimeSpanOptions}
						data={timeSpanOptions}
						mb={'xs'}
					/>
					<SegmentedControl
						value={displayOption}
						onChange={setDisplayOption}
						data={displayOptions}
						mb={'xs'}
					/>
				</Group>
			}
		>
			<Stack h={'90%'}>
				<Skeleton
					visible={topTenQuery.isLoading || !topTenQuery.isSuccess}
					h={boxProps.h}
				>
					{displayOption === displayOptions[0].value ? (
						<BarChart
							{...defaultChartProps}
							series={transactionsSeries}
							valueFormatter={(v) => Math.round(v).toString()}
						/>
					) : (
						<BarChart
							series={salesSeries}
							{...defaultChartProps}
							valueFormatter={(v) => formatToCurrency(v)}
						/>
					)}
				</Skeleton>
			</Stack>
		</Paper>
	);
}

function mapSalesToChartFormat(
	selectedTimeSpan: string,
	averages?: ApiResponse<AverageGraphDto[]>,
): Record<string, any>[] {
	if (averages == null || averages.value == null) {
		return [];
	}

	if (selectedTimeSpan === displayOptions[0].value) {
		return averages.value.map((average) => {
			return {
				name: average.independentVariable,
				transactions: average.transactions,
			};
		});
	}

	if (selectedTimeSpan === displayOptions[1].value) {
		return averages.value.map((average) => {
			return {
				name: average.independentVariable,
				sales: average.sales,
			};
		});
	}

	return averages.value.map((average) => {
		return {
			name: average.independentVariable,
			sales: average.sales,
			transactions: average.transactions,
		};
	});
}
